import { useState, useCallback } from "react";
import { AxiosError } from "axios";
import { UnicoResource } from "@repo/core/services/resources/unico";
import {
  UnicoCreateProcessDTO,
  UnicoCreateProcessResponse
} from "@repo/core/services/entities/Unico";
import { newApInstance } from "@repo/core/services/instances/new-api";
import useToast from "@repo/core/components/core/Toast";
import {
  UseUnicoCreateProcessParams,
  UseUnicoCreateProcessReturn,
  UnicoRetryExhaustedError
} from "./useUnicoCreateProcess.types";
import {
  isUnicoUnavailableError,
  getUnicoErrorMessage
} from "./unico-error-handler";
import {
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS,
  UNICO_UNAVAILABLE_MESSAGE
} from "./unico-create-process.constants";

const unicoResource = new UnicoResource(newApInstance);

/**
 * Custom hook to handle Unico process creation with retry logic and automatic fallback to manual capture
 *
 * @param params - Hook parameters with callbacks
 * @returns Hook return object with createProcess function and state
 */
export const useUnicoCreateProcess = (
  params?: UseUnicoCreateProcessParams
): UseUnicoCreateProcessReturn => {
  const toast = useToast();
  const { onSuccess, onError, onUnavailable } = params || {};

  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState<Error | AxiosError | null>(null);

  /**
   * Delay function for retry attempts
   */
  const delay = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setIsLoading(false);
    setRetryCount(0);
    setError(null);
  }, []);

  /**
   * Handle retry exhaustion - switch to manual capture
   */
  const handleRetryExhausted = useCallback(
    (lastError: Error | AxiosError) => {
      console.error(
        `Unico createProcess failed after ${MAX_RETRY_ATTEMPTS} attempts:`,
        lastError
      );

      toast.error(UNICO_UNAVAILABLE_MESSAGE);
      onUnavailable?.(lastError);
    },
    [onUnavailable, toast]
  );

  /**
   * Create Unico process with retry logic
   */
  const createProcess = useCallback(
    async (
      payload: UnicoCreateProcessDTO
    ): Promise<UnicoCreateProcessResponse | void> => {
      setIsLoading(true);
      setError(null);
      setRetryCount(0);

      let lastError: Error | AxiosError | null = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          const response = await unicoResource.createProcess(payload);
          const data = response.data;

          // Success - reset retry count and call success callback
          setRetryCount(0);
          setIsLoading(false);

          if (onSuccess) {
            onSuccess(data);
          }

          return data;
        } catch (err) {
          lastError = err as Error | AxiosError;
          setRetryCount(attempt);
          setError(lastError);

          const isUnavailable = isUnicoUnavailableError(err);

          console.error(
            `Unico createProcess attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed:`,
            {
              error: err,
              isUnavailable,
              status: (err as AxiosError)?.response?.status
            }
          );

          // If error indicates unavailability and we have retries left, retry
          if (isUnavailable && attempt < MAX_RETRY_ATTEMPTS) {
            await delay(RETRY_DELAY_MS);
            continue;
          }

          // If error is not retryable or retries exhausted
          if (!isUnavailable) {
            // Non-retryable error - call error callback and stop
            setIsLoading(false);
            if (onError) {
              onError(lastError);
            } else {
              const errorMessage = getUnicoErrorMessage(err);
              toast.error(errorMessage);
            }
            throw lastError;
          }

          // If we reach here, retries are exhausted
          if (attempt === MAX_RETRY_ATTEMPTS) {
            setIsLoading(false);
            handleRetryExhausted(lastError);

            const retryExhaustedError = new UnicoRetryExhaustedError(
              `Unico createProcess failed after ${MAX_RETRY_ATTEMPTS} attempts`,
              MAX_RETRY_ATTEMPTS,
              lastError
            );

            if (onError) {
              onError(retryExhaustedError);
            }

            throw retryExhaustedError;
          }
        }
      }

      // This should never be reached, but TypeScript needs it
      setIsLoading(false);
      if (lastError) {
        handleRetryExhausted(lastError);
        throw lastError;
      }
    },
    [onSuccess, onError, handleRetryExhausted, toast]
  );

  return {
    createProcess,
    isLoading,
    retryCount,
    error,
    reset
  };
};
